import { create } from 'zustand';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { Issue } from 'src/services/interfaces/application';
import { HealthCondition } from 'src/services/interfaces/health';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useInsuranceStore } from 'src/store/insurance';
import { useApplicationStore } from 'src/store/application';

// Global object for storing form references
export const globalForms: Record<
  string,
  { reset: (data: Partial<OnboardingSchema>) => void }
> = {};

type OnboardingState = Partial<OnboardingSchema> & {
  currentStep: number;
  currentStepId: string | null;
  totalSteps: number;
  data: Partial<OnboardingSchema>;
  availableSteps: number;
  totalPlanPrice: number;
  isSliderStep: boolean;
  activeSliderTab: number;
  totalSliderTabs: number;
  isLoaded: boolean;
  isLastSelectedExtraPlan: string | null;
  isLastSelectedPHQ: boolean;
  phqSubmitted: boolean;
  medicalTags: string[];
  planTags: Record<string, string[]>;
  aiHighlightedPlanUid: string | null;
  activeDentalVisionCategory: number;
  errorCount: number;
  finishStepKey: string | null;
  setData: (formData: Partial<OnboardingSchema>) => void;
  resetData: () => void;
  setCurrentStep: (step: number) => void;
  setTotalSteps: (steps: number) => void;
  setAvailableSteps: (steps: number) => void;
  setSliderStep: (isSlider: boolean) => void;
  setActiveSliderTab: (tab: number) => void;
  setTotalSliderTabs: (tabs: number) => void;
  resetAllForms: (data: Partial<OnboardingSchema>) => void;
  registerForm: (
    id: string,
    resetFn: (data: Partial<OnboardingSchema>) => void
  ) => void;
  unregisterForm: (id: string) => void;
  setLastSelectedExtraPlan: (categoryKey: string | null) => void;
  setLastSelectedPHQ: (isLastSelectedPHQ: boolean) => void;
  setPhqSubmitted: (submitted: boolean) => void;
  setMedicalTags: (tags: string[]) => void;
  setPlanTags: (planId: string, tags: string[]) => void;
  rejectMedicalPlan: () => void;
  removeMedicalPlan: () => void;
  enhanceHealthQuestionsData: (data: Partial<OnboardingSchema>, issues: Issue[], conditions: HealthCondition[]) => Partial<OnboardingSchema>;
  setAiHighlightedPlan: (planUid: string | null) => void;
  setActiveDentalVisionCategory: (id: number) => void;
  incrementErrorCount: () => void;
  decrementErrorCount: () => void;
  setCurrentStepId: (id: string | null) => void;
  setFinishStepKey: (key: string | null) => void;
};

export const useOnboardingStore = create<OnboardingState>((set) => ({
  data: {},
  currentStep: 0,
  currentStepId: null,
  totalSteps: 0,
  availableSteps: 0,
  isSliderStep: false,
  activeSliderTab: 0,
  totalSliderTabs: 0,
  totalPlanPrice: 0,
  isLoaded: false,
  isLastSelectedExtraPlan: null,
  isLastSelectedPHQ: false,
  phqSubmitted: false,
  medicalTags: [],
  planTags: {},
  aiHighlightedPlanUid: null,
  activeDentalVisionCategory: 0,
  errorCount: 0,
  finishStepKey: null,

  // Функция для автоматического заполнения полей здоровья на основе issues, conditions и effectiveDate
  enhanceHealthQuestionsData: (data: Partial<OnboardingSchema>, issues: Issue[], conditions: HealthCondition[]) => {
    let enhancedData = { ...data };

    // Проверяем наличие issues и effectiveDate
    if (issues && issues.length > 0 && data.healthQuestions?.coverageStartDate?.effectiveDate) {
      // Если issues и effectiveDate не пустые, устанавливаем surgeriesDiabetesHeartMental-none в true
      if (!enhancedData.healthQuestions) {
        enhancedData.healthQuestions = {
          initialQuestions: { treatment: false, invalid: false, pregnancy: false },
          takingMedicationsNow: { value: false },
          surgeriesDiabetesHeartMental: { conditions: [] },
          privacyDisclosure: { accepted: false, disclaimer: false, signature: '' }
        };
      }
      if (!enhancedData.healthQuestions.surgeriesDiabetesHeartMental) {
        enhancedData.healthQuestions.surgeriesDiabetesHeartMental = { conditions: [] };
      }
      if (!enhancedData.healthQuestions.surgeriesDiabetesHeartMental.conditions) {
        enhancedData.healthQuestions.surgeriesDiabetesHeartMental.conditions = [];
      }

      // Добавляем значение 'none' если его нет
      const hasNoneValue = enhancedData.healthQuestions.surgeriesDiabetesHeartMental.conditions.some(
        (item: any) => item.value === 'none'
      );
      if (!hasNoneValue) {
        enhancedData.healthQuestions.surgeriesDiabetesHeartMental.conditions.push({ value: 'none' });
      }
    }

    // Проверяем наличие conditions и effectiveDate
    if (conditions && conditions.length > 0 && data.healthQuestions?.coverageStartDate?.effectiveDate) {
      // Если conditions и effectiveDate не пустые, устанавливаем otherHealthConditions-none в true
      if (!enhancedData.healthQuestions) {
        enhancedData.healthQuestions = {
          initialQuestions: { treatment: false, invalid: false, pregnancy: false },
          takingMedicationsNow: { value: false },
          surgeriesDiabetesHeartMental: { conditions: [] },
          privacyDisclosure: { accepted: false, disclaimer: false, signature: '' }
        };
      }
      if (!enhancedData.healthQuestions.otherHealthConditions) {
        enhancedData.healthQuestions.otherHealthConditions = { conditions: [] };
      }
      if (!enhancedData.healthQuestions.otherHealthConditions.conditions) {
        enhancedData.healthQuestions.otherHealthConditions.conditions = [];
      }

      // Добавляем значение 'none' если его нет
      const hasNoneValue = enhancedData.healthQuestions.otherHealthConditions.conditions.some(
        (item: any) => item.value === 'none'
      );
      if (!hasNoneValue) {
        enhancedData.healthQuestions.otherHealthConditions.conditions.push({ value: 'none' });
      }
    }

    return enhancedData;
  },

  rejectMedicalPlan: () => {
    const data = useOnboardingStore.getState().data;
    const newData = { ...data };
    newData.medicalPlanRejected = true;

    set({ data: newData });
  },

  removeMedicalPlan: () => {
    const data = useOnboardingStore.getState().data;
    const newData = { ...data };

    newData.medicalPlan = '';
    newData.medicalPlanPrice = 0;
    newData.medicalPlanTier = '';
    newData.medicalPlansToCompare = [];

    set({ data: newData });
  },

  setData: (data) =>
    set((state) => {
      const newData = { ...state.data, ...data };

      const medicalPlanPrice = Number(newData.medicalPlanPrice) || 0;
      console.log('medicalPlanPrice', medicalPlanPrice)
      const dentalPlanPrice = Number(newData.dentalPlanPrice) || 0;
      console.log('dentalPlanPrice', dentalPlanPrice)
      const legalPlanPrice = Number(newData.legalPlanPrice) || 0;
      const identityProtectionPlanPrice =
        Number(newData.identityProtectionPlanPrice) || 0;
      const criticalIllnessPlanPrice =
        Number(newData.criticalillnessPlanPrice) || 0;

      const totalPlanPrice =
        medicalPlanPrice +
        dentalPlanPrice +
        legalPlanPrice +
        identityProtectionPlanPrice +
        criticalIllnessPlanPrice;

      state.isLoaded = true;

      // Обновляем все зарегистрированные формы только если данные действительно изменились
      const hasDataChanged = JSON.stringify(state.data) !== JSON.stringify(newData);
      if (hasDataChanged) {
        const plans = useInsuranceStore.getState().plans || [];
        const transformedApplication = transformApplicationData(newData, plans);

        useApplicationStore.setState((state) => {
          const existingApplications = state.applications || [];
          const currentApplication = existingApplications[0];
          const mergedApplication = currentApplication
            ? {
                ...currentApplication,
                ...transformedApplication,
                id: currentApplication.id,
              }
            : transformedApplication;

          const isSame =
            currentApplication &&
            JSON.stringify(currentApplication) === JSON.stringify(mergedApplication);

          if (isSame) {
            return {};
          }

          const nextApplications =
            existingApplications.length > 0
              ? [mergedApplication, ...existingApplications.slice(1)]
              : [mergedApplication];

          return {
            applications: nextApplications,
          };
        });

        Object.values(globalForms).forEach((form) => {
          try {
            form.reset(newData);
          } catch (error) {
            console.warn('Error resetting form:', error);
          }
        });
      }
      console.log('newData', newData);
      return {
        data: newData,
        totalPlanPrice,
      };
    }),

  resetData: () => {
    set({
      data: {},
      currentStep: 0,
      totalSteps: 0,
      availableSteps: 0,
      isSliderStep: false,
      activeSliderTab: 0,
      totalSliderTabs: 0,
      totalPlanPrice: 0,
      medicalTags: [],
      planTags: {},
    });
  },

  setCurrentStep: (step) => {
    console.log('[onboardingStore] setCurrentStep called:', {
      from: useOnboardingStore.getState().currentStep,
      to: step,
    });
    console.trace('[onboardingStore] Stack trace for setCurrentStep');
    return set((state) => ({
      currentStep: step,
      availableSteps: Math.max(step, state.availableSteps),
    }));
  },

  setTotalSteps: (steps) => set({ totalSteps: steps }),
  setAvailableSteps: (steps) => set({ availableSteps: steps }),
  setSliderStep: (isSlider) => set({ isSliderStep: isSlider }),
  setActiveSliderTab: (tab) => set({ activeSliderTab: tab }),
  setTotalSliderTabs: (tabs) => set({ totalSliderTabs: tabs }),

  resetAllForms: (data) => {
    Object.values(globalForms).forEach((form) => form.reset(data));
  },

  registerForm: (id, resetFn) => {
    globalForms[id] = { reset: resetFn };
  },

  unregisterForm: (id) => {
    delete globalForms[id];
  },

  setLastSelectedExtraPlan: (categoryKey) => {
    set(() => ({
      isLastSelectedExtraPlan: categoryKey,
    }));
  },

  setLastSelectedPHQ: (isLastSelectedPHQ) => {
    set(() => ({
      isLastSelectedPHQ,
    }));
  },
  setPhqSubmitted: (submitted) => {
    set(() => ({
      phqSubmitted: submitted,
    }));
  },

  setMedicalTags: (tags) => {
    set(() => ({
      medicalTags: tags,
    }));
  },

  setPlanTags: (planId, tags) => {
    set((state) => ({
      planTags: {
        ...state.planTags,
        [planId]: tags,
      },
    }));
  },

  setAiHighlightedPlan: (planUid) => {
    set(() => ({ aiHighlightedPlanUid: planUid }));
  },

  setActiveDentalVisionCategory: (category) => {
    set(() => ({ activeDentalVisionCategory: category }));
  },

  incrementErrorCount: () => {
    set((state) => {
      const nextCount = (state.errorCount || 0) + 1;
      return {
        errorCount: nextCount
      } as Partial<OnboardingState> as any;
    });
  },

  decrementErrorCount: () => {
    set((state) => {
      const nextCount = Math.max(0, (state.errorCount || 0) - 1);
      return {
        errorCount: nextCount
      } as Partial<OnboardingState> as any;
    });
  },

  setCurrentStepId: (id: string | null) => {
    set((state) =>
      state.currentStepId === id ? state : { currentStepId: id }
    );
  },

  setFinishStepKey: (key: string | null) => {
    set((state) =>
      state.finishStepKey === key ? state : { finishStepKey: key }
    );
  },
}));
